import { useState, useRef, useCallback } from 'react';
import { queryService } from '../../services/queryService';
import { useSnackbar } from '../../components/common/Snackbar';
import { MAX_TOTAL_SIZE, ALLOWED_FILE_TYPES, EXTENSION_MIME_FALLBACK, ALLOWED_FILE_TYPES_LABEL } from '../../constants/raiseQueryForm';

/**
 * Hook to manage response state and file handling in ViewQueryModal
 * @param {string} queryId - Query ID
 * @param {boolean} open - Whether modal is open
 * @param {boolean} canRespond - Whether user can respond
 * @param {function} onQueryUpdate - Callback when query is updated
 * @param {function} onTimelineFetch - Callback to fetch timeline
 * @returns {Object} Response state and handlers
 */
export const useQueryResponse = (queryId, open, canRespond, onQueryUpdate, onTimelineFetch) => {
  const { showSnackbar } = useSnackbar();
  const [responseMessage, setResponseMessage] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [isSubmittingResponse, setIsSubmittingResponse] = useState(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [responseError, setResponseError] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  // Clear response state when modal closes
  const clearResponseState = useCallback(() => {
    setAttachments([]);
    setResponseMessage('');
    setResponseError('');
    setIsDragging(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  // File type validation (same logic as raise query)
  const isValidFileType = useCallback((file) => {
    if (ALLOWED_FILE_TYPES.includes(file.type)) return true;
    const ext = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
    return ext in EXTENSION_MIME_FALLBACK;
  }, []);

  // File handling with validation
  const handleFileSelect = useCallback((files) => {
    const fileArray = Array.from(files || []);
    if (fileArray.length === 0) return;

    const validFiles = [];
    const errors = [];
    const currentTotalSize = attachments.reduce((sum, f) => sum + f.size, 0);

    fileArray.forEach((file) => {
      if (!isValidFileType(file)) {
        errors.push(`${file.name}: Only ${ALLOWED_FILE_TYPES_LABEL} files are allowed`);
        return;
      }
      const newTotal = currentTotalSize + validFiles.reduce((s, f) => s + f.size, 0) + file.size;
      if (newTotal > MAX_TOTAL_SIZE) {
        errors.push(`Total file size exceeds ${MAX_TOTAL_SIZE / (1024 * 1024)}MB limit (all files combined)`);
        return;
      }
      validFiles.push(file);
    });

    if (errors.length > 0) {
      showSnackbar(errors.join('. '), 'error', { duration: 5000 });
    }
    if (validFiles.length > 0) {
      setAttachments((prev) => [...prev, ...validFiles]);
    }
  }, [attachments, isValidFileType, showSnackbar]);

  const handleAttachmentChange = useCallback((event) => {
    handleFileSelect(event.target.files);
    // Reset input to allow selecting the same file again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [handleFileSelect]);

  const handleRemoveAttachment = useCallback((index) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleDragOver = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);
    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      handleFileSelect(files);
    }
  }, [handleFileSelect]);

  const handleFileInputClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  // Response submission
  const handleResponseSubmit = useCallback(async (event) => {
    event.preventDefault();

    // Backend always requires a message, even when files are attached
    if (!responseMessage.trim()) {
      setResponseError('Please enter your response message before submitting.');
      return;
    }

    setIsSubmittingResponse(true);
    setResponseError('');

    try {
      let payload;
      let config = {};

      if (attachments.length > 0) {
        // Use FormData when files are present
        const multipartData = new FormData();
        // Always send the message (backend requires it)
        multipartData.append('message', responseMessage.trim());
        
        // Append each file - using 'attachments' key (multiple files with same key name)
        attachments.forEach((file) => {
          multipartData.append('attachments', file);
        });
        
        payload = multipartData;
        config = {};
      } else {
        // Use JSON when no files
        payload = {
          message: responseMessage.trim(),
          attachments: [],
        };
      }

      const updatedQuery = await queryService.respondToQuery(queryId, payload, config, false); // isDraft = false
      if (onQueryUpdate) {
        onQueryUpdate(updatedQuery);
      }
      if (onTimelineFetch) {
        onTimelineFetch({ silent: true });
      }
      
      setResponseMessage('');
      setAttachments([]);
      clearResponseState();
      showSnackbar('Response submitted successfully and visible to raiser', 'success');
    } catch (error) {
      const message =
        error.response?.data?.message ||
        error.message ||
        'Failed to submit response. Please try again.';
      showSnackbar(message, 'error');
    } finally {
      setIsSubmittingResponse(false);
    }
  }, [responseMessage, attachments, queryId, onQueryUpdate, onTimelineFetch, clearResponseState, showSnackbar]);

  return {
    responseMessage,
    setResponseMessage,
    attachments,
    setAttachments,
    isSubmittingResponse,
    isSavingDraft,
    setIsSavingDraft,
    responseError,
    setResponseError,
    isDragging,
    fileInputRef,
    handleFileSelect,
    handleAttachmentChange,
    handleRemoveAttachment,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    handleFileInputClick,
    handleResponseSubmit,
    clearResponseState,
  };
};
